import React from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { get } from "lodash-es";
import {
  Controller,
  FieldArrayWithId,
  FieldPathByValue,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { JsonSchemaRowAdvancedOptions } from "src/aiNode/editorComponents/JsonSchemaRowAdvancedOptions";
import { AiNodeJsonSchemaPropertyUI, AiNodeV2Form } from "src/aiNode/types";
import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { Icon } from "src/base-components/Icon";
import { ReorderHandle } from "src/base-components/ReorderHandle";
import { Option, Select } from "src/base-components/Select";
import { Textarea } from "src/base-components/Textarea";
import { SCHEMA_TYPE_ICONS } from "src/base-components/TypeIcons";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import {
  isArrayOfPrimitive,
  isObject,
  isObjectWithProperties,
  isPrimitive,
  isUnionOfPrimitivesOnly,
  JsonSchema,
  JsonSchemaNode,
} from "src/jsonSchema/types";
import { JSONSchemaRenderer } from "src/jsonSchema/viewer";
import { createTitleOnlyHeaderRenderer } from "src/jsonSchema/viewer/JSONSchemaRenderer";
import { SchemaOptions } from "src/router/SearchParams";
import { type SchemaEnumErrorStatus } from "src/schema/SchemaEnumRow";
import { SchemaEnumRows } from "src/schema/SchemaEnumRows";
import { PropertyConverter, PropertyUI } from "src/schema/schemaMappingUtils";

type JsonSchemaRowProps = {
  id: string;
  immutable: boolean;
  handleRemove: () => void;
  index: number;
  formLocation: string; // react-hook-forms path for binding the row data
  typeOptions: Option[]; // available in the dropdown
  fieldData: FieldArrayWithId<
    AiNodeV2Form,
    FieldPathByValue<AiNodeV2Form, AiNodeJsonSchemaPropertyUI[]>
  >; // pass the field data directly to avoid useWatch sync issues
  onChange?: () => void; // called when field changes
};

export const JsonSchemaRow: React.FC<JsonSchemaRowProps> = ({
  id,
  immutable,
  handleRemove,
  index,
  formLocation,
  typeOptions,
  fieldData,
  onChange,
}) => {
  const { listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id,
    });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const {
    register,
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();

  const outputId = getValues(`${formLocation}.outputId`);
  const isFieldNameHighlighted = useIsFieldHighlighted(`${outputId}:field`);
  const isDescriptionHighlighted = useIsFieldHighlighted(
    `${outputId}:description`,
  );

  const field = fieldData;
  const formErrors = get(errors, formLocation) ?? {};

  const propertyEnum = useWatch({
    control,
    name: `${formLocation}.enum` as const,
  });

  const propertyUI = field as PropertyUI;
  const schemaNode = PropertyConverter.uiToBEProperty(
    propertyUI,
    SchemaOptions.Output,
  ) as JsonSchemaNode;
  const fullSchema = propertyUI._schema as JsonSchema;

  const treePreviewSchema = {
    ...(fullSchema ?? {}),
    properties: {
      [propertyUI.fieldName]: schemaNode,
    },
  };

  // Add "custom" option if the current type is a union
  const [currentType] = propertyUI.type || [];
  const effectiveTypeOptions =
    currentType === "union"
      ? [
          ...typeOptions,
          {
            key: "union",
            value: "custom", // Note: value is actually the label
            icon: SCHEMA_TYPE_ICONS["union"],
            disabled: true, // Not selectable
          },
        ]
      : typeOptions;

  return (
    <div
      ref={setNodeRef}
      className="w-full"
      data-loc={`ai-node-response-schema-${index}`}
      style={style}
    >
      <div className="flex w-full items-center gap-x-2">
        <ReorderHandle immutable={immutable} listeners={listeners} />
        <div className="min-w-0 flex-1">
          <Controller
            control={control}
            name={`${formLocation}.fieldName` as const}
            render={({ field }) => (
              <AutocompleteCodeInput
                dataLoc={`ai-node-field-location-${index}`}
                disabled={immutable}
                error={
                  "fieldName" in formErrors
                    ? (formErrors.fieldName?.message as string)
                    : undefined
                }
                isSearchHighlighted={isFieldNameHighlighted}
                placeholder="field"
                prefix="data."
                transparentBackground={immutable}
                value={field.value ?? ""}
                onChange={(value) => {
                  field.onChange(value);
                  onChange?.();
                }}
              />
            )}
          />
        </div>
        <div className="w-36">
          <Controller
            control={control}
            name={`${formLocation}.type.0` as const}
            render={(props) => (
              <Select
                dataLoc={`ai-node-field-type-${index}`}
                disabled={immutable}
                options={effectiveTypeOptions}
                value={props.field.value}
                useLockNodeEditorClassName
                onChange={(value) => {
                  if (value === "enum" && props.field.value !== "enum") {
                    setValue(`${formLocation}.enum` as const, [
                      { value: '"value_1"' },
                    ]);
                  }
                  if (value !== "enum" && props.field.value === "enum") {
                    setValue(`${formLocation}.enum` as const, undefined);
                  }
                  props.field.onChange(value);
                  onChange?.();
                }}
              />
            )}
          />
        </div>
        <JsonSchemaRowAdvancedOptions
          fieldData={fieldData}
          formLocation={formLocation}
          immutable={immutable}
          index={index}
        />
        <Icon
          color="text-gray-400 hover:text-gray-700"
          dataLoc={`ai-node-field-remove-${index}`}
          icon={faTrashCan}
          size="sm"
          onClick={!immutable ? handleRemove : undefined}
        />
      </div>
      {Array.isArray(propertyEnum) && !isDragging && (
        <SchemaEnumRows
          enumFormLocation={`${formLocation}.enum`}
          errorStatus={
            "enum" in formErrors
              ? (formErrors.enum as SchemaEnumErrorStatus)
              : undefined
          }
          immutable={immutable}
          withValidation
        />
      )}
      <div className="mt-2 ml-7">
        <Textarea
          data-loc={`ai-node-field-description-${index}`}
          disabled={immutable}
          {...register(`${formLocation}.description` as const, {
            onChange: () => onChange?.(),
          })}
          isSearchHighlighted={isDescriptionHighlighted}
          placeholder="Describe what this field represents in the response..."
          fullWidth
        />
      </div>

      {hasJSONTreePreview(schemaNode) && (
        <div className="mt-1 mb-4 ml-7">
          <JSONSchemaRenderer
            displayOptions={{
              pathPrefix: "data",
              maxDepth: 6,
              // Note: we always return the full schema with all keys as node output, so we are effectively
              // ignoring the "required" fields in the user-defined JSON schema. To avoid confusion, we hide the
              // indicators here.
              showRequired: false,
              customLabelAndTypeRenderer: createTitleOnlyHeaderRenderer(),
            }}
            schema={treePreviewSchema}
          />
        </div>
      )}
    </div>
  );
};

const hasJSONTreePreview = (schemaNode: JsonSchemaNode): boolean =>
  schemaNode &&
  !isPrimitive(schemaNode) &&
  !isArrayOfPrimitive(schemaNode) &&
  !isUnionOfPrimitivesOnly(schemaNode) &&
  !(isObject(schemaNode) && !isObjectWithProperties(schemaNode));
